import { jsPDF } from "jspdf";
import type { Workspace } from "../domain/types";
import { APP_VERSION, RULES_VERSION, SCHEMA_VERSION } from "../domain/types";
import { modelLabel } from "../domain/options";
import { isRealLiteracyEvidence } from "../domain/score";
import policyBaseMarkdown from "../content/ki-nutzungsrichtlinie-basis.md?raw";

const disclaimer = "Hinweis: Dieses Dokument ist eine Readiness-Dokumentation. Es ist keine Rechtsberatung, keine Zertifizierung und keine Garantie für EU-AI-Act-Konformität.";

const DEFAULT_POLICY_VERSION = "0.1.0";
const DEFAULT_POLICY_VERSION_LABEL = "Richtlinien-Version";

export type PolicyMarkdownOptions = {
  baseVersion?: string;
  baseVersionLabel?: string;
  documentIdEnabled?: boolean;
};

function hashContent(content: string) {
  let hash = 0x811c9dc5;
  for (let i = 0; i < content.length; i++) {
    hash ^= content.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).toUpperCase().padStart(8, "0");
}

function latestByTimestamp<T extends { updated_at?: string; created_at?: string }>(items: T[]) {
  return [...items].sort((a, b) => {
    const left = Date.parse(a.updated_at ?? a.created_at ?? "");
    const right = Date.parse(b.updated_at ?? b.created_at ?? "");
    return (Number.isFinite(right) ? right : 0) - (Number.isFinite(left) ? left : 0);
  })[0];
}

function profileLines(workspace: Workspace) {
  const profile = workspace.businessProfile[0];
  if (!profile) return ["Business: nicht angegeben"];
  const withCustom = (value: string, custom?: string) => custom ? `${modelLabel(value)} (${custom})` : modelLabel(value);
  const customerTypeLabel = (value: string) => modelLabel(value === "clients" ? "mostly_b2b" : value);
  return [
    `Business: ${profile.business_name || "nicht angegeben"}`,
    `Inhaber:in / Verantwortliche Person: ${profile.owner_name || "nicht angegeben"}`,
    `Dokumentation für: ${profile.documentation_recipient || "nicht angegeben"}`,
    `Ort / Land: ${profile.business_location || "nicht angegeben"}`,
    `Business-Typ: ${withCustom(profile.business_segment, profile.business_segment_custom)}`,
    `Leistungsbereich: ${profile.service_areas.map((area) => withCustom(area, profile.service_area_custom)).join(", ") || "nicht angegeben"}`,
    `Kundenart: ${profile.customer_types.map(customerTypeLabel).join(", ") || "nicht angegeben"}`,
    `KI-Nutzungsgrad: ${withCustom(profile.ai_usage_level, profile.ai_usage_custom)}`,
    `Vorabprofil: ${profile.preliminary_role_profile ? modelLabel(profile.preliminary_role_profile) : "nicht angegeben"}`,
    `Vorab-Wahrscheinlichkeit: Deployer ${profile.preliminary_role_confidence?.deployer ?? 0}% · Provider ${profile.preliminary_role_confidence?.provider ?? 0}%`
  ];
}

function useCaseDisplayId(id: string) {
  return `UC-${id.replace(/^usecase_/, "").slice(0, 8).toUpperCase()}`;
}

function toolDisplayId(id: string) {
  return `TL-${id.replace(/^tool_/, "").slice(0, 8).toUpperCase()}`;
}

function actionItemTitle(item: Workspace["actionItems"][number]) {
  if (item.source_type === "tool" && item.source_id && item.title.includes("Tool-Kategorie")) {
    return `Nutzungsregel für ${toolDisplayId(item.source_id)} dokumentieren`;
  }
  return item.title;
}

function formatDate(value?: string) {
  return value ? new Date(value).toLocaleDateString("de-DE") : "nicht angegeben";
}

function sourceReference(sourceType: string, sourceId?: string) {
  if (!sourceId) return modelLabel(sourceType);
  if (sourceType === "use_case") return `Use Case ${useCaseDisplayId(sourceId)}`;
  if (sourceType === "tool") return `Tool ${toolDisplayId(sourceId)}`;
  return `${modelLabel(sourceType)} ${sourceId}`;
}

function formatActionItem(item: Workspace["actionItems"][number]) {
  const details = [
    `- [${modelLabel(item.status)}] ${actionItemTitle(item)}`,
    `Priorität: ${modelLabel(item.priority)}`,
    `Quelle: ${sourceReference(item.source_type, item.source_id)}`,
    item.completed_at ? `Erledigt am: ${new Date(item.completed_at).toLocaleDateString("de-DE")}` : "",
    item.completion_note ? `Bestätigung: ${item.completion_note}` : ""
  ].filter(Boolean);
  return details.join(" · ");
}

function currentRole(workspace: Workspace) {
  return workspace.roleCheckRecords.find((item) => item.id === "role_check_current") ?? latestByTimestamp(workspace.roleCheckRecords);
}

function currentTransparency(workspace: Workspace) {
  return workspace.transparencyChecks.find((item) => item.id === "transparency_check_current") ?? latestByTimestamp(workspace.transparencyChecks);
}

function policyToolLine(tool: Workspace["aiTools"][number]) {
  return `- ${toolDisplayId(tool.id)} · ${tool.name ? `${tool.name} · ` : ""}${modelLabel(tool.category)} · Status: ${modelLabel(tool.status)} · Nutzung: ${tool.usage_contexts.map(modelLabel).join(", ")} · Datenarten: ${tool.data_categories.map(modelLabel).join(", ")}`;
}

function policyUseCaseLine(item: Workspace["aiUseCases"][number], allTools: Workspace["aiTools"]) {
  return `- ${useCaseDisplayId(item.id)} · ${modelLabel(item.category)} · Kontext: ${modelLabel(item.context)} · Tools: ${linkedToolsLabel(allTools, item)} · Datenarten: ${item.data_categories.map(modelLabel).join(", ")} · Output: ${modelLabel(item.output_type)} · Human Review: ${modelLabel(item.human_review)} · Status: ${item.status === "complete" ? "Aktiv" : modelLabel(item.status)}`;
}

function policyRiskLine(workspace: Workspace) {
  if (!workspace.riskAssessments.length) return "- Noch keine Risiko-Einstufungen vorhanden.";
  return workspace.riskAssessments.map((risk) => `- ${useCaseDisplayId(risk.source_id)} · ${modelLabel(risk.traffic_light)} · ${risk.reasons.map(modelLabel).join(", ")}${risk.red_review_points.length ? ` · Rote Prüfpunkte: ${risk.red_review_points.map(modelLabel).join(", ")}` : ""}`).join("\n");
}

function policyTransparencyLine(workspace: Workspace) {
  const transparency = currentTransparency(workspace);
  if (!transparency) return "- Transparenz-Check noch nicht durchgeführt.";
  const notices = transparency.recommended_notices.length ? transparency.recommended_notices.join(", ") : "keine konkreten Hinweise abgeleitet";
  return [
    `- Status: ${modelLabel(transparency.status)}`,
    `- Kundenbezug: ${modelLabel(transparency.customer_facing)}`,
    `- Öffentlicher Output: ${modelLabel(transparency.public_output)}`,
    `- Direkte KI-Interaktion: ${modelLabel(transparency.direct_ai_interaction)}`,
    `- Empfohlene Hinweise: ${notices}`
  ].join("\n");
}

function policyRoleLine(workspace: Workspace) {
  const role = currentRole(workspace);
  if (!role) return "- Rollenprofil noch nicht geprüft.";
  return [
    `- Rollenprofil: ${modelLabel(role.profile)}`,
    role.ai_act_role_profile ? `- Aktuelles AI-Act-Profil: ${modelLabel(role.ai_act_role_profile)}` : "",
    role.ai_act_role_confidence ? `- AI-Act-Wahrscheinlichkeit: Deployer ${role.ai_act_role_confidence.deployer}% · Provider ${role.ai_act_role_confidence.provider}%` : "",
    `- Erkannte Signale: ${role.signals.length ? role.signals.map(modelLabel).join(", ") : "keine besonderen Signale"}`,
    `- Externe Prüfung: ${role.requires_external_review ? "empfohlen" : "nicht offensichtlich erforderlich"}`
  ].join("\n");
}

function policyLiteracyLine(workspace: Workspace) {
  const latest = latestByTimestamp(workspace.literacyRecords.filter(isRealLiteracyEvidence));
  if (!latest) return "- Noch kein AI-Literacy-Basisnachweis gespeichert.";
  return `- Basisnachweis: ${latest.passed ? "bestanden" : "nicht bestanden"} · Datum: ${latest.completed_at ? new Date(latest.completed_at).toLocaleDateString("de-DE") : "nicht angegeben"} · Wissenscheck: ${latest.quiz_score}/10`;
}

function policyActionLines(workspace: Workspace) {
  if (!workspace.actionItems.length) return "- Noch keine Maßnahmen dokumentiert.";
  return workspace.actionItems.map(formatActionItem).join("\n");
}

function linkedToolsLabel(allTools: Workspace["aiTools"], item: Workspace["aiUseCases"][number]) {
  if (!item.tool_ids?.length) return modelLabel(item.tool_category);
  return item.tool_ids
    .map((id) => {
      const tool = allTools.find((candidate) => candidate.id === id);
      return tool ? tool.name || toolDisplayId(tool.id) : toolDisplayId(id);
    })
    .join(", ");
}

function summaryToolLine(tool: Workspace["aiTools"][number]) {
  return [
    `- ${toolDisplayId(tool.id)}${tool.name ? ` · ${tool.name}` : ""}`,
    `Kategorie: ${modelLabel(tool.category)}`,
    `Status: ${modelLabel(tool.status)}`,
    `Nutzung: ${tool.usage_contexts.map(modelLabel).join(", ") || "nicht angegeben"}`,
    `Datenarten: ${tool.data_categories.map(modelLabel).join(", ") || "nicht angegeben"}`,
    tool.notes ? `Notiz: ${tool.notes}` : ""
  ].filter(Boolean).join(" · ");
}

function summaryUseCaseLine(item: Workspace["aiUseCases"][number], allTools: Workspace["aiTools"]) {
  return [
    `- ${useCaseDisplayId(item.id)}${item.title ? ` · ${item.title}` : ""}`,
    `Kategorie: ${modelLabel(item.category)}`,
    `Kontext: ${modelLabel(item.context)}`,
    `Tool-Kategorie: ${modelLabel(item.tool_category)}`,
    item.tool_ids?.length ? `Verknüpfte Tools: ${linkedToolsLabel(allTools, item)}` : "",
    `Status: ${item.status === "complete" ? "Aktiv" : modelLabel(item.status)}`,
    `Datenarten: ${item.data_categories.map(modelLabel).join(", ") || "nicht angegeben"}`,
    `Output: ${modelLabel(item.output_type)}`,
    `Human Review: ${modelLabel(item.human_review)}`,
    `Kundeninteraktion: ${modelLabel(item.customer_interaction)}`,
    `Automatische Entscheidung über Personen: ${modelLabel(item.automated_decision_about_people)}`,
    item.description ? `Notiz: ${item.description}` : ""
  ].filter(Boolean).join(" · ");
}

function summaryRiskLine(risk: Workspace["riskAssessments"][number]) {
  return [
    `- ${sourceReference(risk.source_type, risk.source_id)}`,
    `Ampel: ${modelLabel(risk.traffic_light)}`,
    `Gründe: ${risk.reasons.map(modelLabel).join(", ") || "keine"}`,
    risk.positive_factors.length ? `Positive Faktoren: ${risk.positive_factors.map(modelLabel).join(", ")}` : "",
    risk.red_review_points.length ? `Rote Prüfpunkte: ${risk.red_review_points.map(modelLabel).join(", ")}` : ""
  ].filter(Boolean).join(" · ");
}

function summaryRoleLines(workspace: Workspace) {
  const role = currentRole(workspace);
  if (!role) return "- Rollen-Check noch nicht durchgeführt.";
  return [
    `- Rollenprofil: ${modelLabel(role.profile)}`,
    `- Erkannte Signale: ${role.signals.length ? role.signals.map(modelLabel).join(", ") : "keine besonderen Signale"}`,
    `- Externe Prüfung empfohlen: ${role.requires_external_review ? "Ja" : "Nein"}`,
    `- Aktualisiert am: ${formatDate(role.updated_at)}`
  ].join("\n");
}

function summaryTransparencyLines(workspace: Workspace) {
  const transparency = currentTransparency(workspace);
  if (!transparency) return "- Transparenz-Check noch nicht durchgeführt.";
  return [
    `- Status: ${modelLabel(transparency.status)}`,
    `- Kundenbezug: ${modelLabel(transparency.customer_facing)}`,
    `- Öffentlicher Output: ${modelLabel(transparency.public_output)}`,
    `- Direkte KI-Interaktion: ${modelLabel(transparency.direct_ai_interaction)}`,
    `- Empfohlene Hinweise: ${transparency.recommended_notices.length ? transparency.recommended_notices.join(", ") : "keine konkreten Hinweise abgeleitet"}`,
    `- Aktualisiert am: ${formatDate(transparency.updated_at)}`
  ].join("\n");
}

function summaryModuleLines(workspace: Workspace) {
  if (!workspace.moduleStatuses.length) return "- Noch keine Modulstatusdaten vorhanden.";
  return workspace.moduleStatuses.map((item) => `- ${item.module}: ${modelLabel(item.state)} · ${item.summary}`).join("\n");
}

function summaryGeneratedDocumentLines(workspace: Workspace, includeAiAssist = true) {
  const documents = includeAiAssist ? workspace.generatedDocuments : workspace.generatedDocuments.filter((doc) => !doc.title.startsWith("AI-Assist:"));
  if (!documents.length) return "- Noch keine generierten Dokumente gespeichert.";
  return documents.map((doc) => [
    `- ${doc.title}`,
    `Typ: ${modelLabel(doc.type)}`,
    doc.source_id ? `Quelle: ${sourceReference(doc.source_id.startsWith("usecase_") ? "use_case" : doc.source_id.startsWith("tool_") ? "tool" : "record", doc.source_id)}` : "",
    `Status: ${modelLabel(doc.status)}`,
    `Aktualisiert am: ${formatDate(doc.updated_at)}`
  ].filter(Boolean).join(" · ")).join("\n");
}

function summaryAiAssistLines(workspace: Workspace) {
  const usage = workspace.aiAssistUsage;
  const privacy = workspace.privacyGateEvents;
  if (!usage.length && !privacy.length) return "- Noch keine AI-Assist-Nutzung dokumentiert.";
  const successful = usage.filter((item) => item.success).length;
  const failed = usage.filter((item) => !item.success).length;
  const latestPrivacy = latestByTimestamp(privacy);
  return [
    `- AI-Assist-Aufrufe: ${usage.length} gesamt · ${successful} erfolgreich · ${failed} fehlgeschlagen`,
    `- Privacy-Gate-Bestätigungen: ${privacy.length}`,
    latestPrivacy ? `- Letztes Privacy Gate: ${modelLabel(latestPrivacy.task_type)} · gesendete Felder: ${latestPrivacy.sent_fields.join(", ") || "keine"} · blockierte Felder: ${latestPrivacy.blocked_fields.join(", ") || "keine"}` : ""
  ].filter(Boolean).join("\n");
}

function summarySettingsLines(workspace: Workspace, includeAiAssist = true) {
  const settings = workspace.appSettings[0];
  if (!settings) return "- App-Einstellungen nicht initialisiert.";
  return [
    `- Storage-Modus: ${settings.storage_mode}`,
    includeAiAssist ? `- AI-Assists übrig: ${settings.credits_remaining}` : "",
    `- Letztes JSON Backup: ${settings.last_backup_at ? formatDate(settings.last_backup_at) : "noch nicht dokumentiert"}`
  ].filter(Boolean).join("\n");
}

function summaryHistoryLines(workspace: Workspace) {
  const exportInfo = workspace.exportHistory.length ? `${workspace.exportHistory.length} Export-Einträge, zuletzt ${formatDate(latestByTimestamp(workspace.exportHistory)?.updated_at)}` : "keine Export-Historie";
  const importInfo = workspace.importHistory.length ? `${workspace.importHistory.length} Import-Einträge, zuletzt ${formatDate(latestByTimestamp(workspace.importHistory)?.imported_at)}` : "keine Import-Historie";
  return [`- ${exportInfo}`, `- ${importInfo}`].join("\n");
}

function dossierTitleLine() {
  const title = "# AI-Act Readiness Summary";
  const meta = `Kit ${APP_VERSION} · Schema ${SCHEMA_VERSION} · Rules ${RULES_VERSION}`;
  return `${title.padEnd(74, " ")}${meta}`;
}

export function buildDossierMarkdown(workspace: Workspace, includeArchived = false, options: { includeAiAssist?: boolean } = {}) {
  const includeAiAssist = options.includeAiAssist ?? true;
  const tools = includeArchived ? workspace.aiTools : workspace.aiTools.filter((tool) => tool.status !== "archived");
  const useCases = includeArchived ? workspace.aiUseCases : workspace.aiUseCases.filter((item) => item.status !== "archived");
  const score = latestByTimestamp(workspace.readinessScores);
  const role = currentRole(workspace);
  const transparency = currentTransparency(workspace);
  const latestLiteracy = latestByTimestamp(workspace.literacyRecords.filter(isRealLiteracyEvidence));
  const redPoints = workspace.riskAssessments.flatMap((risk) => risk.red_review_points);
  return [
    dossierTitleLine(),
    "",
    disclaimer,
    "",
    `Stand: ${new Date().toLocaleDateString("de-DE")}`,
    "",
    "## Dokumentationsprofil",
    profileLines(workspace).join("\n"),
    "",
    "## Zusammenfassung",
    `Readiness Score: ${score?.score ?? 0}/100`,
    score?.explanation ?? "Der Score misst Dokumentationsfortschritt.",
    `Rollenprofil: ${role ? modelLabel(role.profile) : "noch nicht geprüft"}`,
    `Transparenzstatus: ${transparency ? modelLabel(transparency.status) : "noch nicht geprüft"}`,
    "",
    "## Modulstatus",
    summaryModuleLines(workspace),
    "",
    "## Tool-Register",
    tools.length ? tools.map(summaryToolLine).join("\n") : "- Noch keine Tools dokumentiert.",
    "",
    "## Use Cases",
    useCases.length ? useCases.map((item) => summaryUseCaseLine(item, workspace.aiTools)).join("\n") : "- Noch keine Use Cases dokumentiert.",
    "",
    "## Risiko-Ampel",
    workspace.riskAssessments.length ? workspace.riskAssessments.map(summaryRiskLine).join("\n") : "- Noch keine Risiko-Einstufungen vorhanden.",
    "",
    "## Rollen-Check",
    summaryRoleLines(workspace),
    "",
    "## Transparenz-Check",
    summaryTransparencyLines(workspace),
    "",
    "## Offene rote Prüfpunkte",
    redPoints.length ? redPoints.map((point) => `- Externe Prüfung empfohlen: ${modelLabel(point)}`).join("\n") : "- Keine roten Prüfpunkte dokumentiert.",
    "",
    "## AI-Literacy",
    latestLiteracy ? [
      `- Basisnachweis: ${latestLiteracy.passed ? "bestanden" : "nicht bestanden"}`,
      `- Selbstbestätigung: ${latestLiteracy.self_confirmed ? "Ja" : "Nein"}`,
      `- Wissenscheck: ${latestLiteracy.quiz_score}/10`,
      `- Abgeschlossen am: ${formatDate(latestLiteracy.completed_at)}`,
      `- Themen: ${latestLiteracy.topics_completed.join(", ")}`
    ].join("\n") : "- Noch kein AI-Literacy-Basisnachweis gespeichert.",
    "",
    "## Maßnahmen",
    workspace.actionItems.length ? workspace.actionItems.map(formatActionItem).join("\n") : "- Noch keine Maßnahmen dokumentiert.",
    "",
    includeAiAssist ? "## Generierte Dokumente und AI-Assist-Ergebnisse" : "## Generierte Dokumente",
    summaryGeneratedDocumentLines(workspace, includeAiAssist),
    "",
    includeAiAssist ? "## AI-Assist und Privacy Gate" : "",
    includeAiAssist ? summaryAiAssistLines(workspace) : "",
    includeAiAssist ? "" : "",
    "## Workspace-Einstellungen",
    summarySettingsLines(workspace, includeAiAssist),
    "",
    "## Export-/Import-Historie",
    summaryHistoryLines(workspace)
  ].join("\n");
}
export function buildPolicyMarkdown(workspace: Workspace, includeArchived = false, options: PolicyMarkdownOptions = {}) {
  const tools = includeArchived ? workspace.aiTools : workspace.aiTools.filter((tool) => tool.status !== "archived");
  const useCases = includeArchived ? workspace.aiUseCases : workspace.aiUseCases.filter((item) => item.status !== "archived");
  const baseVersion = options.baseVersion?.trim() || DEFAULT_POLICY_VERSION;
  const baseVersionLabel = options.baseVersionLabel?.trim() || DEFAULT_POLICY_VERSION_LABEL;

  const individualContent = [
    "### Geltungsbereich und Dokumentationsprofil",
    profileLines(workspace).join("\n"),
    "",
    "### Dokumentierte KI-Tools",
    tools.length ? tools.map(policyToolLine).join("\n") : "- Noch keine KI-Tools dokumentiert.",
    "",
    "### Dokumentierte Use Cases",
    useCases.length ? useCases.map((item) => policyUseCaseLine(item, workspace.aiTools)).join("\n") : "- Noch keine Use Cases dokumentiert.",
    "",
    "### Risikoeinstufung und rote Prüfpunkte",
    policyRiskLine(workspace),
    "",
    "### Rollenprofil",
    policyRoleLine(workspace),
    "",
    "### Transparenz gegenüber Kunden und Öffentlichkeit",
    policyTransparencyLine(workspace),
    "",
    "### Human Review und Verantwortlichkeit",
    useCases.length ? useCases.map((item) => `- ${useCaseDisplayId(item.id)} · Human Review: ${modelLabel(item.human_review)} · kunden-/öffentlichkeitsrelevanter Output: ${["client_ready_text", "public_content", "automatic_customer_reply"].includes(item.output_type) ? "ja" : "nicht offensichtlich"}`).join("\n") : "- Noch keine Use Cases mit Human-Review-Status dokumentiert.",
    "",
    "### AI-Literacy und Dokumentationsnachweis",
    policyLiteracyLine(workspace),
    "",
    "### Maßnahmen, offene Punkte und externe Prüfung",
    policyActionLines(workspace)
  ].join("\n");

  const header = [
    "# KI-Nutzungsrichtlinie",
    "",
    `${baseVersionLabel}: ${baseVersion}`,
    ...(options.documentIdEnabled ? [`Dokument-ID: DOC-${hashContent(individualContent)}`] : []),
    `Stand: ${new Date().toLocaleString("de-DE")}`
  ].join("\n");

  return [
    header,
    "",
    disclaimer,
    "",
    policyBaseMarkdown.trim(),
    "",
    "## Individuelle Erfassung aus dem AI-Act Readiness KIT",
    "",
    individualContent,
    "",
    "### Aktualisierung",
    "- Diese Richtlinie sollte aktualisiert werden, wenn neue KI-Tools, neue Use Cases, geänderte Datenarten, neue rote Prüfpunkte oder abgeschlossene Maßnahmen dokumentiert werden.",
    "- Das JSON Backup bleibt der vollständige technische Sicherungsstand. Diese Richtlinie ist eine exportierbare Arbeitsrichtlinie."
  ].join("\n");
}
export function toolsToCsv(workspace: Workspace, includeArchived = false) {
  const tools = includeArchived ? workspace.aiTools : workspace.aiTools.filter((tool) => tool.status !== "archived");
  const rows = [["ID", "Toolname", "Kategorie", "Status", "Nutzungskontext", "Datenarten"]];
  tools.forEach((tool) => rows.push([toolDisplayId(tool.id), tool.name ?? "", modelLabel(tool.category), modelLabel(tool.status), tool.usage_contexts.map(modelLabel).join("; "), tool.data_categories.map(modelLabel).join("; ")]));
  return rows.map((row) => row.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(",")).join("\n");
}

export function actionItemsToCsv(workspace: Workspace) {
  const rows = [["Titel", "Status", "Priorität", "Quelle", "Bestätigung", "Erledigt am"]];
  workspace.actionItems.forEach((item) => rows.push([
    actionItemTitle(item),
    modelLabel(item.status),
    modelLabel(item.priority),
    item.source_type,
    item.completion_note ?? "",
    item.completed_at ? new Date(item.completed_at).toLocaleDateString("de-DE") : ""
  ]));
  return rows.map((row) => row.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(",")).join("\n");
}

export function markdownToPdf(markdown: string, title: string) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 40;
  const contentWidth = pageWidth - marginX * 2;
  const topY = 72;
  const bottomY = pageHeight - 72;

  function drawPageFrame() {
    doc.setDrawColor(170, 184, 176);
    doc.setLineWidth(0.7);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("AI-Act-Readiness-KIT", marginX, 32);
    doc.line(marginX, 48, pageWidth - marginX, 48);
    doc.line(marginX, pageHeight - 44, pageWidth - marginX, pageHeight - 44);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(title, marginX, pageHeight - 24);
  }

  drawPageFrame();
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(title, marginX, 78);
  let y = 112;

  function ensureSpace(lineHeight: number) {
    if (y + lineHeight <= bottomY) return;
    doc.addPage();
    drawPageFrame();
    y = topY;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
  }

  markdown.split("\n").forEach((rawLine, index) => {
    const trimmed = rawLine.trimEnd();
    const summaryTitleMatch = index === 0 ? trimmed.match(/^#\s+(AI-Act Readiness Summary)\s+(Kit .*)$/) : null;
    if (summaryTitleMatch) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text(summaryTitleMatch[1], marginX, y);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text(summaryTitleMatch[2], pageWidth - marginX, y, { align: "right" });
      y += 22;
      return;
    }
    const isHeading = /^#{1,3}\s/.test(trimmed);
    const text = trimmed.replace(/^#{1,3}\s/, "");
    if (!text.trim()) {
      y += 8;
      return;
    }

    if (isHeading) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(trimmed.startsWith("# ") ? 13 : 11);
    } else {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
    }

    const lineHeight = isHeading ? 16 : 14;
    const wrapped = doc.splitTextToSize(text, contentWidth) as string[];
    wrapped.forEach((line) => {
      ensureSpace(lineHeight);
      doc.text(line, marginX, y);
      y += lineHeight;
    });
    if (isHeading) {
      y += 4;
    }
  });
  return doc.output("blob");
}
